import {ISpecificDialogsService, DialogResult, IConfirmLugasCoolingOffLogoutDialogOptions} from "./specific-dialogs.service.interface";
import {ServiceBase} from "../ServiceBase";
import {DialogButtonClickHandleResultEnum, IModalDialogHandler} from "../dialog/dialog.service.interface";
import {LugasActivePopupComponent} from "../../components/lugas/lugas-active-popup.component";
import {
    PRIORITY_LEVEL,
    PRIORITY_LEVEL_ZINDEX_MAPPER
} from "../../../sdk/src/services/overlay_manager/overlay_manager_levels";
import React from "react";
import {
    ACTION_TAKEN_NAMES,
    DISPLAY_MESSAGE_IDS,
    DISPLAY_MESSAGE_NAMES
} from "../../../common/const/casinofied-analytics-consts";
import {InfoIcon} from "../../themes/defaults/icons/info.icon";
import {LoginPopupComponent} from "../../components/login-popup/login-popup.component";
import {ILoginPopupData} from "../login-popup/login-popup.service.interface";
import {ModifyBiometricDialogComponent} from "../../components/biometric-dialog/modify-biometric-dialog/modify-biometric-dialog.component";

export class SpecificDialogsService extends ServiceBase implements ISpecificDialogsService {
    enforceHybridAppModal(analyticsOnClick?: {
        category: string;
        action?: string;
        label?: string;
        value?: any;
        omitStructuredLogger?: boolean;
    }
    ) {
        let DownloadAppIcon = this.services.theme.currentTheme.icons.sideMenuDownloadApp;
        return this.services.dialog.showSimpleTextModal({
            title: this.services.translation.translate('ENFORCE_HYBRID_APP_TITLE'),
            text: this.services.translation.translate('ENFORCE_HYBRID_APP_TEXT'),
            iconSvg: DownloadAppIcon,
            buttons: [
                {
                    text: this.services.translation.translate('ENFORCE_HYBRID_APP_CONTINUE_BUTTON'),
                    buttonPreset: 'primary',
                    onClick: async () => {
                        if (analyticsOnClick) {
                            const {category, action, label, value, omitStructuredLogger} = analyticsOnClick;
                            this.services.analytics.sendAnalytics(category, action, label, value, omitStructuredLogger);
                        }
                        await this.services.externalLinks.openDownloadApp();
                        return DialogButtonClickHandleResultEnum.KeepDialogOpen;
                    }
                }
            ]
        });
    }

    showLugasCooldownPopup(): IModalDialogHandler {
        const businessCorrelationId = this.services.tools.getGuid();
        const reportConfirmClicked = () => {
            this.services.casinofiedAnalytics.messageActionTaken({
                businessCorrelationID: businessCorrelationId,
                correlationID: businessCorrelationId,
                actionType: ACTION_TAKEN_NAMES.CONFIRM_ACTION,
                displayMessageName: DISPLAY_MESSAGE_NAMES.LUGAS_COOLDOWN_MESSAGE_NAME
            })
        }
        const ret = this.services.dialog.showSimpleTextModal({
            buttons: [
                {
                    buttonPreset: "primary",
                    text: this.services.translation.translate('LUGAS_OK_BTN'),
                    onClick: async () => {
                        reportConfirmClicked();
                        return DialogButtonClickHandleResultEnum.CloseDialogAndContinue;
                    }
                }
            ],
            text: this.services.translation.translate('LUGAS_COOLDOWN_TEXT'),
            title: this.services.translation.translate('LUGAS_COOLDOWN_TITLE'),
            iconSvg: this.services.theme.currentTheme.icons.warningSignIcon,
            disableFullscreenMobile: true,
            showPopupAboveSecondWebViewHybrid: true,
            onClosedByXButton: reportConfirmClicked,
        })

        this.services.casinofiedAnalytics.messageDisplay({
            businessCorrelationID: businessCorrelationId,
            correlationID: businessCorrelationId,
            displayMessageID: DISPLAY_MESSAGE_IDS.lugasCooldown,
            displayMessageName: DISPLAY_MESSAGE_NAMES.LUGAS_COOLDOWN_MESSAGE_NAME
        })

        return ret;
    }

    showLugasActivePopup(): IModalDialogHandler {
        const businessCorrelationId = this.services.tools.getGuid();
        const reportConfirmClicked = () => {
            this.services.casinofiedAnalytics.messageActionTaken({
                businessCorrelationID: businessCorrelationId,
                correlationID: businessCorrelationId,
                actionType: ACTION_TAKEN_NAMES.CONFIRM_ACTION,
                displayMessageName: DISPLAY_MESSAGE_NAMES.LUGAS_ACTIVE_MESSAGE_NAME
            })
        }
        const ret = this.services.dialog.showModal(dialogHandler =>
            <LugasActivePopupComponent
                dialogHandler={dialogHandler}
            />,
            {
                title: this.services.translation.translate('LUGAS_ACTIVE_TITLE'),
                icon: this.services.theme.currentTheme.icons.warningSignIcon,
                forceTransparentBackground: true,
                disableFullscreenMobile: true,
                forceCentralization: true,
                mobileCenterHeader: true,
                isHighPriority: true,
                forceZOrder: PRIORITY_LEVEL_ZINDEX_MAPPER[PRIORITY_LEVEL.TOUCH_ID] - 1,
                shouldBeClosedOnlyByUser: true,
                onClosed: reportConfirmClicked
            }
        );

        this.services.casinofiedAnalytics.messageDisplay({
            businessCorrelationID: businessCorrelationId,
            correlationID: businessCorrelationId,
            displayMessageID: DISPLAY_MESSAGE_IDS.lugasActiveOnAnotherOperator,
            displayMessageName: DISPLAY_MESSAGE_NAMES.LUGAS_ACTIVE_MESSAGE_NAME
        })

        return ret;
    }

    async showConfirmLugasCoolingOffLogout(options: IConfirmLugasCoolingOffLogoutDialogOptions): Promise<DialogResult> {
        const translation = this.services.translation;
        return new Promise<DialogResult>((resolve, reject) => {
            const businessCorrelationId = this.services.tools.getGuid();
            const reportClick = (action) => {
                this.services.casinofiedAnalytics.messageActionTaken({
                    businessCorrelationID: businessCorrelationId,
                    correlationID: businessCorrelationId,
                    actionType: action,
                    displayMessageName: DISPLAY_MESSAGE_NAMES.LUGAS_LOGOUT_MESSAGE_NAME
                })
            }
            this.services.dialog.showSimpleTextModal({
                title: translation.translate('LUGAS_COOLING_OFF_LOGOUT_TITLE_LOGOUT'),
                text: translation.translate('LUGAS_COOLING_OFF_LOGOUT_MESSAGE'),
                icon: (<InfoIcon />),
                hideCloseButton: false,
                buttons: [
                    {
                        text: this.services.translation.translate('CANCEL'),
                        buttonPreset: 'primary',
                        onClick: async () => {
                            try {
                                await options.onReject();
                                reportClick(ACTION_TAKEN_NAMES.CANCEL_ACTION);
                                resolve(DialogResult.Rejected);
                            } catch (err) {
                                reject(err);
                            }

                            return DialogButtonClickHandleResultEnum.CloseDialogAndContinue;
                        }
                    },
                    {
                        text: translation.translate('LOGOUT'),
                        buttonPreset: 'info',
                        onClick: async () => {
                            try {
                                await options.onAccept();
                                reportClick(ACTION_TAKEN_NAMES.CONFIRM_ACTION);
                                resolve(DialogResult.Accepted);
                            } catch (err) {
                                reject(err);
                            }

                            return DialogButtonClickHandleResultEnum.CloseDialogAndContinue;
                        }
                    }
                ]
            })

            this.services.casinofiedAnalytics.messageDisplay({
                businessCorrelationID: businessCorrelationId,
                correlationID: businessCorrelationId,
                displayMessageID: DISPLAY_MESSAGE_IDS.lugasLogout,
                displayMessageName: DISPLAY_MESSAGE_NAMES.LUGAS_LOGOUT_MESSAGE_NAME
            })

        })
    }

    showLoginPopup(confirmClicked: () => void): IModalDialogHandler {
        const businessCorrelationId = this.services.tools.getGuid();
        const reportConfirmClicked = () => {
            confirmClicked && confirmClicked();

            this.services.casinofiedAnalytics.messageActionTaken({
                businessCorrelationID: businessCorrelationId,
                correlationID: businessCorrelationId,
                actionType: ACTION_TAKEN_NAMES.CONFIRM_ACTION,
                displayMessageName: DISPLAY_MESSAGE_NAMES.LOGIN_MESSAGE
            })
        }

        const ret = this.services.dialog.showModal(dialogHandler =>
            <LoginPopupComponent
                onApprove={reportConfirmClicked}
                dialogHandler={dialogHandler}
            />,
            {
                title: this.services.translation.translate("LOGIN_POPUP_TITLE"),
                icon: this.services.theme.currentTheme.icons.warningSignIcon,
                forceTransparentBackground: true,
                disableFullscreenMobile: true,
                forceCentralization: true,
                mobileCenterHeader: true,
                isHighPriority: true,
                forceZOrder: PRIORITY_LEVEL_ZINDEX_MAPPER[PRIORITY_LEVEL.TOUCH_ID] - 1,
                shouldBeClosedOnlyByUser: true,
                hideCloseButton: true
            }
        );

        const data: ILoginPopupData = this.services.loginPopup.getLoginPopupData();
        const amountBankroll = this.services.walletProxy.totalBankroll;
        this.services.casinofiedAnalytics.messageDisplay({
            businessCorrelationID: businessCorrelationId,
            correlationID: businessCorrelationId,
            displayMessageID: DISPLAY_MESSAGE_IDS.loginMessage,
            displayMessageName: DISPLAY_MESSAGE_NAMES.LOGIN_MESSAGE,
            extraData: {
                totalStakes: data.TotalStakes,
                netPosition: data.NetPosition,
                totalWins: data.TotalWins,
                totalLosses: data.TotalLosses,
                bankrollCurr: data.BankrollCurr,
                amountBankroll: amountBankroll
            }
        })

        return ret;
    }

    showModifyBiometricDialog(): IModalDialogHandler {
        return this.services.dialog.showModal(dialogHandler => <ModifyBiometricDialogComponent
            dialogHandler={dialogHandler}
        />, {
            title: this.services.translation.translate('TOUCH_ID_AUTO_SCREEN'),
            forceTransparentBackground: true,
            disableFullscreenMobile: true,
            forceCentralization: true,
            shouldBeClosedOnlyByUser: true,
            mobileCenterHeader: true,
            onClosedByXButton: () => this.services.sdk.eventsTrigger.closeModifyBimetricDialog(),
        });
    }
}